import { arrayToTree, SelectWithAll, useTranslation } from "@opendash/core";
import { Card, Statistic } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Issuecategory,
  Ticket_Issuecategory,
  Ticket_Kanban_State_Current,
} from "../../../js/parsegenerated";
import { useDurationFormat } from "../hooks/useDurationFormat";

/**
 * MTBF (mean time before failure) is the average time it takes for the system to fail.
 */
export default observer(function MeanTimeBeforeFailure(props: {
  ticketEndStates: Ticket_Kanban_State_Current[];
}) {
  const t = useTranslation();
  const [mtbf, setMTBF] = useState(0);
  const [issues, setIssues] = useState<Issuecategory[]>([]);
  const [selectedIssueIds, setSelectedIssueIds] = useState<(string | number)[]>(
    []
  );
  const formatMTBF = useDurationFormat(mtbf);

  // calculate MTBF
  useEffect(() => {
    async function getMTBF() {
      const filteredTicketEndStates: Ticket_Kanban_State_Current[] = [];
      for (const ticketEndState of props.ticketEndStates) {
        // check issue category
        const ticketIssue = await new Parse.Query(Ticket_Issuecategory)
          .equalTo("ticket", ticketEndState.get("ticket"))
          .first();

        // if issue category is not selected, skip
        if (
          !selectedIssueIds.some(
            (issueID) => ticketIssue.get("issuecategory").id === issueID
          )
        )
          continue;

        // add ticket state to filtered list
        filteredTicketEndStates.push(ticketEndState);
      }

      // not enough tickets found, so no MTBF
      if (filteredTicketEndStates.length <= 1) {
        setMTBF(0);
        return;
      }

      console.log("filteredTicketEndStates", filteredTicketEndStates);

      // calculate MTBF
      let totalCount = 0;
      let totalValue = 0;

      // first sort the ticketStates by last update time.
      // This is necessary to calculate the time between the last update of a ticket and
      // the first update of the next ticket
      filteredTicketEndStates.sort(
        (a, b) => b.get("updatedAt")?.getTime() - a.get("updatedAt")?.getTime()
      );

      let lastTicketTime: number = undefined;
      filteredTicketEndStates.forEach((ticketEndState) => {
        const ticketBegin = ticketEndState.get("createdAt")?.getTime() ?? 0;
        const ticketEnd = ticketEndState.get("updatedAt")?.getTime() ?? 0;

        if (!lastTicketTime || !ticketEnd) {
          lastTicketTime = ticketEnd;
          return;
        }

        totalCount++;
        totalValue += lastTicketTime - ticketBegin;
        lastTicketTime = ticketEnd;
      });

      setMTBF(totalCount > 0 ? totalValue / totalCount : 0);
    }

    getMTBF();
  }, [props.ticketEndStates, selectedIssueIds]);

  useEffect(() => {
    async function getIssues() {
      const issues = await new Parse.Query(Issuecategory)
        .equalTo("enabled", true)
        .includeAll()
        .find();
      setIssues(issues);
    }

    getIssues();
  }, []);

  // map issues to tree structure
  const issueOptions = useMemo(() => {
    const tree = arrayToTree(
      issues,
      (issue) => issue.get("parent")?.id,
      (issue) => issue.id
    );

    // recursive function to map tree to tree select options
    function mapTreeElement(
      issue: Issuecategory & { children?: Issuecategory[] }
    ) {
      return {
        label: issue.get("name"),
        value: issue.id,
        children: issue.children?.map(mapTreeElement),
      };
    }

    return tree.map(mapTreeElement);
  }, [issues]);

  return (
    <>
      {/* 
// @ts-ignore */}
      <Card variant="outlined">
        <Statistic
          title={
            <>
              {t("maintenance:stats.mean_time_before_failure.statistic.title")}
              <SelectWithAll
                style={{ width: "100%" }}
                isTree
                options={issueOptions}
                value={selectedIssueIds}
                onChange={setSelectedIssueIds}
                allLabel={t("maintenance:issuecategory.all")}
                autoSelectDepth={Infinity}
                placeholder={t("maintenance:stats.issuecategory.select")}
              />
            </>
          }
          value={formatMTBF}
        />
      </Card>
    </>
  );
});
